import React, { FunctionComponent, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { FormContext } from '../../Form/Context';
import { Fieldset } from '../Fieldset';
import {
    SubFormField,
    SubFormFieldMapper,
    SubFormFieldParser,
    useSubFormField,
} from '../../Field/useSubFormField';

export type ArrayFieldsetProps = {
    name: string;
    children: Array<ReactNode>;
};

type ArraySubFormFieldType<SubType extends Record<string, any> = Record<string, any>> =
    SubFormField<Array<SubType>, SubType>;

export const ArrayFieldset: FunctionComponent<ArrayFieldsetProps> = ({ name, children }) => {
    const fieldValueFromFormValues = useCallback<SubFormFieldMapper<ArraySubFormFieldType>>(
        (subFormValues) => {
            return Object.entries(subFormValues).reduce(
                (arrayValue: Array<any>, [stringIndex, value]) => {
                    arrayValue[Number(stringIndex)] = value;
                    return arrayValue;
                },
                []
            );
        },
        []
    );

    const formValuesToFieldValue = useCallback<SubFormFieldParser<ArraySubFormFieldType>>(
        (fieldValue) => {
            return fieldValue.reduce((subFormValues, value, index) => {
                return { ...subFormValues, [String(index)]: value };
            }, {});
        },
        []
    );

    const { formContext } = useSubFormField({
        name,
        fieldValueFromFormValues,
        formValuesToFieldValue,
    });

    const wrappedChildren = useMemo(() => {
        return children.map((child, index) => {
            return (
                <Fieldset key={index} name={String(index)}>
                    {child}
                </Fieldset>
            );
        });
    }, [children]);

    return <FormContext.Provider value={formContext}>{wrappedChildren}</FormContext.Provider>;
};
